// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import BoardCollabInviteCell from 'app/common/react/components/BoardCollab/BoardCollabInviteCell';
import ga from 'app/common/lib/ga';
import type { Dispatch } from 'redux';
import { Box } from 'gestalt';
import { compose } from 'redux';
import { HEADER_ZINDEX } from 'app/common/react/components/Header/HeaderConstants';
import { type Invite } from 'app/common/react/components/BoardCollab/types';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import {
  approveBoardCollabInvite,
  deleteBoardCollabInvite,
} from 'app/common/redux/actions/board/collab';
import {
  RemoveOtherFromBoard,
  RemoveSelfFromBoardModal,
} from 'app/common/react/components/BoardEdit/BoardInvites/CollaboratorButtonForm';

const GA_CATEGORY_DECLINE_INVITE = 'collaborator_decline_invite';
const GA_CATEGORY_DELETE_INVITE = 'collaborator_delete_invite';

type OwnProps = {|
  invite: Invite,
  isDim?: boolean,
  onBoardLeave: () => void,
  onInviteDidUpdate: () => void,
  onModalOpen?: () => void,
  useGestaltModal?: boolean,
|};

type State = {|
  currentModal: null | 'delete',
  loading: boolean,
|};

type HOCProps = {|
  viewer: Viewer,
|};

// approve/delete are curried when mapping from redux actions
type ReduxProps = {|
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  approveBoardCollabInvite: () => Promise<*>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  deleteBoardCollabInvite: (isViewerInvitedUser: boolean, shouldBan: boolean) => Promise<*>,
|};

type Props = {|
  ...OwnProps,
  ...HOCProps,
  ...ReduxProps,
|};

/*
 * BoardCollabInviteCellContainer is a container that handles group board collaborator/invite
 * actions such as approving, removing, leaving, etc.
 */
export class BoardCollabInviteCellContainer extends React.Component<Props, State> {
  state = {
    currentModal: null,
    loading: false,
  };

  onDelete = () => {
    const { onModalOpen } = this.props;
    if (onModalOpen) {
      onModalOpen();
    }

    this.setState({ currentModal: 'delete' });
  };

  onApprove = () => {
    const { approveBoardCollabInvite, onInviteDidUpdate } = this.props;

    this.setState({
      loading: true,
    });

    approveBoardCollabInvite()
      .then(onInviteDidUpdate)
      .finally(() => {
        this.setState({
          loading: false,
        });
      });
  };

  get isViewerInvitedUser(): boolean {
    const {
      viewer,
      invite: { invited_user },
    } = this.props;
    return viewer.isAuth && viewer.id === invited_user.id;
  }

  get isRequestToJoin(): boolean {
    const {
      invite: { invited_user, invited_by_user },
    } = this.props;
    return invited_user.id === invited_by_user.id;
  }

  deleteBoardInvite = (shouldBan: boolean = false) => {
    const { deleteBoardCollabInvite, onBoardLeave, onInviteDidUpdate } = this.props;
    const isViewerInvitedUser = this.isViewerInvitedUser;

    const gaCategory = isViewerInvitedUser ? GA_CATEGORY_DECLINE_INVITE : GA_CATEGORY_DELETE_INVITE;
    ga.trackEvent(gaCategory, PCONST.GA_ACTION_CLICK);

    this.setState({
      currentModal: null,
      loading: true,
    });

    deleteBoardCollabInvite(isViewerInvitedUser, shouldBan)
      .then(() => {
        ga.trackEvent(gaCategory, PCONST.GA_ACTION_SUCCESS);
        onInviteDidUpdate();
        if (isViewerInvitedUser) {
          onBoardLeave();
        }
      })
      .catch(() => {
        ga.trackEvent(gaCategory, PCONST.GA_ACTION_ERROR);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  handleDeleteBoardInvite = () => {
    this.deleteBoardInvite(false);
  };

  handleDeleteBoardInviteAndBan = () => {
    this.deleteBoardInvite(true);
  };

  handleHideModal = () => {
    this.setState({ currentModal: null });
  };

  renderCurrentModal() {
    const {
      invite: { invited_user },
      useGestaltModal = false,
    } = this.props;
    const { currentModal } = this.state;

    if (currentModal === 'delete') {
      if (this.isViewerInvitedUser) {
        return (
          <div style={{ position: 'absolute', zIndex: HEADER_ZINDEX + 1 }}>
            <RemoveSelfFromBoardModal
              onCancel={this.handleHideModal}
              onRemove={this.handleDeleteBoardInvite}
              useGestaltModal={useGestaltModal}
            />
          </div>
        );
      } else {
        return (
          <div style={{ position: 'absolute', zIndex: HEADER_ZINDEX + 1 }}>
            <RemoveOtherFromBoard
              removeType={this.isRequestToJoin ? 'declineRequest' : 'removeCollaborator'}
              firstName={invited_user.first_name}
              fullName={invited_user.full_name}
              onCancel={this.handleHideModal}
              onRemove={this.handleDeleteBoardInvite}
              onRemoveAndBlock={this.handleDeleteBoardInviteAndBan}
              useGestaltModal={useGestaltModal}
              asModal
            />
          </div>
        );
      }
    }

    return null;
  }

  render() {
    const { invite, isDim } = this.props;
    const { loading } = this.state;

    return (
      <Box>
        <BoardCollabInviteCell
          invite={invite}
          isDim={isDim && invite.status !== 'accepted'}
          onDelete={this.onDelete}
          onApprove={this.onApprove}
          actionsDisabled={loading}
        />
        {this.renderCurrentModal()}
      </Box>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>, props: OwnProps) => {
  const {
    invite,
    invite: {
      invited_user: { id: invitedUserId },
      board: { id: boardId },
    },
  } = props;

  return {
    approveBoardCollabInvite: () => dispatch(approveBoardCollabInvite(boardId, invitedUserId)),
    deleteBoardCollabInvite: (isViewerInvitedUser: boolean, shouldBan: boolean) =>
      dispatch(deleteBoardCollabInvite(invite, isViewerInvitedUser, shouldBan)),
  };
};

export default compose(
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  connect<*, OwnProps, _, _, _, _>(
    null,
    mapDispatchToProps,
  ),
  withViewer,
)(BoardCollabInviteCellContainer);
